<template>
	<div class="coupons-container">
		<AppTable
			ref="products-table"
			:filters="filtersActive ? filters : null"
			:table-headings="productsHeadings"
			:title="getString('warehouse_products')"
			:total-items="filteredProducts.length"
			:items="currentShownProducts"
			@export="downloadProducts"
			@changed-page="v => currentPageProducts = v"
			@changed-page-size="v => pageSizeProducts = v"
			@changed-sort="changeSort"
			@changed-filter="changeFilter"
		>
			<template #custom-header>
				<h3>{{ getString('warehouse_products') }}</h3>
				<div class="header-actions">
					<AppButtonIcon prevent-change color="var(--color-bg-primary-1)" icon-name="icon-download-blue" @click="downloadProductsPopup" />
					<AppButton class="ml-12" :text="getString('save')" @click="updateWarehouseItems" />
				</div>
			</template>
			<template #before-table>
				<div class="row align-center space-between row-actions" :class="{bottomSpace: filtersActive}">
					<div class="left-column">
						<AppButton icon-left="icon-plus" small outline :text="getString('add_product')" @click="addProductActive = true" />
					</div>
					<div class="right-column">
						<AppTextInput ref="search-input" icon-name="icon-magnify" :placeholder="getString('search')" @changed-text="changeSearch" />
						<AppButtonIcon class="ml-8" icon-name="icon-filters" @click="filtersActive = !filtersActive" />
					</div>
				</div>
			</template>
			<template #editableNumber="{item, heading}">
				<div class="edit-number" v-if="item.stockEdit">
					<input :ref="'edit-stock-'+item.menu_item_warehouse_id" type="number" inputmode="decimal" :value="item.stock ? item.stock+'' : ''" @focusout="endEditStock(item)" placeholder="0" />
				</div>
				<div class="table-edit-display" v-else @click="editStock(item)">
					<div class="standard-table-p">
						{{ item.stock }}
					</div>
				</div>
			</template>
			<template #editableCurrency="{item, heading}">
				<div class="edit-number" v-if="item.buyEdit">
					<input :ref="'edit-price-'+item.menu_item_warehouse_id" type="number" inputmode="decimal" :value="item.buy_price ? currencyStore.dbToActualValue(item.buy_price)+'' : ''" @focusout="endEditBuyPrice(item)" placeholder="0" />
				</div>
				<div class="table-edit-display" v-else @click="editBuyPrice(item)">
					<div class="standard-table-p">
						{{ currencyStore.readableCurrencyValue(item.buy_price) }}
					</div>
				</div>
			</template>
			<template #editableUnit="{item, heading}">
				<div class="table-edit-display" @click="editUnitItem = item">
					<div class="standard-table-p">
						{{ item.unit_of_measure }}
					</div>
				</div>
			</template>
			<template #settingsIcon="{item, heading}">
				<div class="row centered">
					<AppTableButtonGroup :buttons="buttonsSettingItem" :item="item" @picked-setting-item="settingsItem = item"/>
				</div>
			</template>
			<template #infoIcon="{item, heading}">
				<div class="row centered">
					<AppTableButtonGroup :buttons="buttonsInfoLog" :item="item" @picked-info-log="infoItem = item"/>
				</div>
			</template>
		</AppTable>
		<AppBottomSheet :force-width="bottomSheetWidth" :active="addProductActive"  @toggling-popup="toggleAddProduct" >
			<WarehouseMenuEditor v-if="addProductActive" @changed-products="fetchProducts()" />
		</AppBottomSheet>
        <AppBottomSheet :force-width="bottomSheetSmallWidth" :active="editUnitItem !== null"  @toggling-popup="toggleEditUnitItem" >
            <WarehouseUnitOfMeasurePicker v-if="editUnitItem !== null" :current-unit="editUnitItem.unit" @chosen="(v) => chosenUnit(v)" />
        </AppBottomSheet>
		<AppBottomSheet :force-width="bottomSheetSmallWidth" :active="settingsItem !== null"  @toggling-popup="toggleSettings" >
			<WarehouseProductSettings v-if="settingsItem !== null" :product="settingsItem" @changed-product="endEditSettings" />
		</AppBottomSheet>
		<AppBottomSheet :force-width="bottomSheetWidth" :active="infoItem !== null"  @toggling-popup="toggleInfo" >
			<WarehouseProductLogs v-if="infoItem !== null" :product="infoItem" @exit="infoItem = null" />
		</AppBottomSheet>
	</div>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppTable from "@/shared/components/AppTable.vue";
import {useCurrencyStore, useLanguageStore, useNotificationStore, useScriptStore} from "@/shared/stores";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import WarehouseMenuEditor from "@/console-new/components/config/warehouse/WarehouseMenuEditor.vue";
import {useUserStore, useWarehouseStore} from "@/console-new/stores";
import {formatDateMMDDHHmm, formatDateOnly} from "@/shared/helpers/dates";
import {exportToFile} from "@/shared/helpers/excel";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import WarehouseUnitOfMeasurePicker from "@/console-new/components/config/warehouse/WarehouseUnitOfMeasurePicker.vue";
import WarehouseProductSettings from "@/console-new/components/config/warehouse/WarehouseProductSettings.vue";
import WarehouseProductLogs from "@/console-new/components/config/warehouse/WarehouseProductLogs.vue";
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
        const notificationStore = useNotificationStore();
        const warehouseStore = useWarehouseStore();
        const currencyStore = useCurrencyStore();
		const scriptStore = useScriptStore();
		return {
			getString: languageStore.getString,
			currencyStore: currencyStore,
			userStore,
            notificationStore,
			warehouseStore,
			isActive: false,
			scriptStore,
		}
	},
	name: "WarehouseContainer",
	components: {
		AppTableButtonGroup,
		WarehouseProductSettings,
        ModalHeader, WarehouseUnitOfMeasurePicker, WarehouseProductLogs,
        WarehouseMenuEditor, AppBottomSheet, AppTextInput, AppButtonIcon, AppCard, AppTable, AppButton},
	mixins: [WindowWidth],
	data() {
		return {
			pageSizeProducts: 10,
			currentPageProducts: 1,
			productsHeadings: [
				{
					text: this.getString('product_name'),
					fieldName: 'name',
					sortable: true,
					cols: 2,
				},
				{
					text: this.getString('sku'),
					fieldName: 'sku',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('stock'),
					fieldName: 'stock',
					sortable: true,
					cols: 1,
					slotName: 'editableNumber',
					noPadding: true,
				},
				{
					text: this.getString('unit'),
					fieldName: 'unit_of_measure',
					sortable: false,
					cols: 1,
                    slotName: 'editableUnit',
                    noPadding: true,
				},
				{
					text: this.getString('buy_price'),
					fieldName: 'buy_price',
					sortable: true,
					cols: 1,
					formatter: 'currency',
					slotName: 'editableCurrency',
					noPadding: true,
				},
				{
					text: this.getString('sell_price'),
					fieldName: 'price',
					sortable: true,
					cols: 1,
					formatter: 'currency',
				},
				{
					text: this.getString('profit'),
					fieldName: 'profit',
					sortable: true,
					cols: 1,
					formatter: 'currency',
				},
				{
					text: this.getString('last_update'),
					fieldName: 'last_update',
					sortable: true,
					cols: 1,
					formatter: 'date',
				},
				{
					text: this.getString('settings'),
					sortable: false,
					cols: 1,
					fieldName: 'settings',
					slotName: 'settingsIcon',
				},
				{
					text: this.getString('info'),
					sortable: false,
					cols: 1,
					fieldName: 'info',
					slotName: 'infoIcon',
				},
			],
			filters: [
				{
					type: "quantity",
					key: "stock",
					text: this.getString('stock'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "price",
					key: "profit",
					text: this.getString('profit'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "date",
					key: 'last_update',
					tableName: 'warehouse',
					text: this.getString('last_update'),
					finalValue: '',
					active: false
				},
			],
			addProductActive: false,
            editUnitItem: null,
			searchProducts: null,
			sortField: "name",
			sortAsc: true,
			selectedFilters: null,
			filtersActive: false,
			settingsItem: null,
			infoItem: null,
		}
	},
	computed: {
		buttonsSettingItem() {
			return [
				{
					iconName: "icon-settings-blue",
					emitName: "picked-setting-item",
					title: this.getString('setting'),
				},
			];
		},
		buttonsInfoLog() {
			return [
				{
					iconName: "info-blue",
					emitName: "picked-info-log",
					title: this.getString('info_log'),
				},
			];
		},
		currentShownProducts() {
			return this.filteredProducts.slice((this.currentPageProducts - 1) * this.pageSizeProducts, this.currentPageProducts * this.pageSizeProducts);
		},
		filteredProducts() {
			let products = this.warehouseStore.warehouseProducts;

			if(this.sortField) {
				if(this.sortField === "name" || this.sortField === "sku") {
					products.sort((a, b) => {
						if(!a[this.sortField]) {
							return 1;
						}
						if(!b[this.sortField]) {
							return -1;
						}
						let comp = a[this.sortField].localeCompare(b[this.sortField], undefined, {numeric: this.sortField === "sku"});
						if(!this.sortAsc) {
							comp = -comp;
						}
						return comp;
					});
				} else {
					products.sort((a, b) => {
						let comp = a[this.sortField] - b[this.sortField];
						if(!this.sortAsc) {
							comp = -comp;
						}
						return comp;
					});
				}
			}

			if(this.searchProducts) {
				products = products.filter(p => {
					return p.name.toLowerCase().includes(this.searchProducts.toLowerCase()) || p.sku.toLowerCase().includes(this.searchProducts.toLowerCase());
				});
			}

			return this.filterProducts(products);
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
        bottomSheetSmallWidth() {
            if(this.windowWidth < 550) {
                return "100%";
            } else if(this.windowWidth < 768) {
                return "550px";
            } else {
                return "720px";
            }
        },
	},
	methods: {
		filterProducts(productsArray) {
			if(!productsArray || !productsArray.length || !this.selectedFilters || !Object.values(this.selectedFilters).length) {
				return productsArray;
			}

			for(let filterField in this.selectedFilters) {
				let filterValue = this.selectedFilters[filterField];
				if(filterValue) {
					if (filterField.includes("last_update")) {
						let filterDate = new Date(filterValue.replace(/[<>]/, ""));
						productsArray = productsArray.filter(b => {
							return filterField.startsWith("warehouse") ? b.last_update < filterDate : b.last_update > filterDate
						});
					}
					else {
						if(filterValue.startsWith('<')) {
							let finalValue = Number(filterValue.replace('<', ''));
							productsArray = productsArray.filter(b=>b[filterField] < finalValue);
						} else if(filterValue.startsWith('>')) {
							let finalValue = Number(filterValue.replace('>', ''));
							productsArray = productsArray.filter(b=>b[filterField] > finalValue);
						} else {
							let finalValue = Number(filterValue);
							productsArray = productsArray.filter(b=>b[filterField] === finalValue);
						}
					}
				}
			}
			return productsArray;
		},
		changeSearch(v) {
			if(v.length > 0) {
				this.searchProducts = v;
			} else {
				this.searchProducts = null;
			}
		},
		changeSort(newSortField, newSortAsc) {
			this.currentPageProducts = 1;
			this.sortField = newSortField;
			this.sortAsc = !!newSortAsc;
			this.$refs['products-table'].$refs['pagination-btns'].activePage = 1;
		},
		changeFilter(newFilters) {
			this.currentPageProducts = 1;
			this.selectedFilters = newFilters;
			this.$refs['products-table'].$refs['pagination-btns'].activePage = 1;
		},
		editStock(item) {
			item.stockEdit = true;
			this.$nextTick(() => {
				if(this.$refs.hasOwnProperty("edit-stock-"+item.menu_item_warehouse_id)) {
					this.$refs["edit-stock-"+item.menu_item_warehouse_id].focus();
				}
			});
		},
		endEditStock(item) {

			if(this.$refs.hasOwnProperty("edit-stock-"+item.menu_item_warehouse_id)) {
				let value = this.$refs["edit-stock-"+item.menu_item_warehouse_id].value;
				value = Number(value);
				if(value < -1000000000) {
					value = -1000000000;
				} else if(value > 1000000000) {
					value = 1000000000;
				}
				if(value !== item.stock) {
					item.stock = value;
					this.warehouseStore.savedEdits[item.menu_item_warehouse_id] = {
						stock: item.stock,
						buy_price: item.buy_price,
						unit: item.unit,
					};
				}
			}
			item.stockEdit = false;

		},
		editBuyPrice(item) {
			item.buyEdit = true;
			this.$nextTick(() => {
				if(this.$refs.hasOwnProperty("edit-price-"+item.menu_item_warehouse_id)) {
					this.$refs["edit-price-"+item.menu_item_warehouse_id].focus();
				}
			});
		},
		endEditBuyPrice(item) {

			if(this.$refs.hasOwnProperty("edit-price-"+item.menu_item_warehouse_id)) {
				let value = this.$refs["edit-price-"+item.menu_item_warehouse_id].value;
				value = this.currencyStore.actualToDbValue(value);
				if(value < 0) {
					value = 0;
				} else if(value > 1000000000) {
					value = 1000000000;
				}
				if(value !== item.buy_price) {
					item.buy_price = value;
                    item.profit = item.price - item.buy_price;
					this.warehouseStore.savedEdits[item.menu_item_warehouse_id] = {
						stock: item.stock,
						buy_price: item.buy_price,
						unit: item.unit,
					};
				}
			}
			item.buyEdit = false;

		},
		toggleAddProduct(isOpen) {
			if(!isOpen) {
				this.addProductActive = false;
			}
		},
        toggleEditUnitItem(isOpen) {
            if(!isOpen) {
                this.editUnitItem = null;
            }
        },
		toggleSettings(isOpen) {
			if(!isOpen) {
				this.settingsItem = null;
			}
		},
		toggleInfo(isOpen) {
			if(!isOpen) {
				this.infoItem = null;
			}
		},
        chosenUnit(v) {
            if(this.editUnitItem !== null && this.editUnitItem.unit !== v) {
                this.editUnitItem.unit = v;
                this.editUnitItem.unit_of_measure = v;
                if(this.editUnitItem.unit === "pz") {
                    this.editUnitItem.unit_of_measure = this.getString('pieces');
                }
				this.warehouseStore.savedEdits[this.editUnitItem.menu_item_warehouse_id] = {
					stock: this.editUnitItem.stock,
					buy_price: this.editUnitItem.buy_price,
					unit: this.editUnitItem.unit,
				};
            }
            this.editUnitItem = null;
        },
		endEditSettings() {
			this.settingsItem = null;
			this.fetchProducts();
		},
		fetchProducts(callback = null) {
			this.warehouseStore.getWarehouse(callback)
		},
        updateWarehouseItems() {
            if(!this.userStore.currBusinessId) {
                return;
            }
            let items = Object.entries(this.warehouseStore.savedEdits);
            if(items.length) {
                let itemsToSend = [];
                items.forEach(([menu_item_warehouse_id, i]) => {
                    itemsToSend.push({
                        menu_item_warehouse_id: menu_item_warehouse_id,
                        stock: i.stock,
                        buy_price: i.buy_price,
                        unit: i.unit,
                    });
                });
				this.warehouseStore.updateWarehouseItems(itemsToSend, success => {
					if(success) {
						this.notificationStore.showNotification("generic-success");
						this.fetchProducts();
					} else {
						this.notificationStore.showNotification("generic-error");
					}
				})
            } else {
                this.notificationStore.showNotification("nothing-to-save");
            }
        },
        downloadProducts(expExtension) {
            let sym = this.currencyStore?.currCurrency?.symbol;
            let fileName = this.getString("warehouse").toLowerCase()+"_"+this.userStore.currBusiness.name.toLowerCase().replace(/ /g, "_")+"_"+formatDateOnly(new Date()).replace(/ /g, "_");
			exportToFile({
				columns: [
					{ header: this.getString('name'), key: 'name' },
					{ header: this.getString('sku'), key: 'sku' },
					{ header: this.getString('stock'), key: 'stock', type: 'number' },
					{ header: this.getString('unit_of_measure'), key: 'unit_of_measure' },
					{ header: this.getString('buy_price') + " " + sym, value: p => this.currencyStore.dbToActualValue(p.buy_price), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('sell_price') + " " + sym, value: p => this.currencyStore.dbToActualValue(p.price), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('profit') + " " + sym, value: p => this.currencyStore.dbToActualValue(p.profit), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('last_update'), value: p => formatDateMMDDHHmm(p.last_update) },
				],
				rows: this.warehouseStore.warehouseProducts,
				fileName,
				sheetName: this.getString("warehouse"),
				format: expExtension,
				onEmpty: () => this.notificationStore.showNotification('table-no-data'),
			});
        },
		downloadProductsPopup() {
			this.$refs['products-table'].$refs['export-popup'].toggleStatus(true);
		}
	},
	mounted() {
		this.fetchProducts();
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.fetchProducts();
			}
		},
	}
}
</script>

<style scoped lang="scss">
h3 {
	font-size: 18px;
}
.header-actions {
	display: flex;
	align-items: center;
	justify-content: flex-end;
}
.row-actions {
	padding: 10px;
	gap: 8px;

	&.bottomSpace {
		margin-bottom: 12px;
	}

	.right-column {
		display: flex;
		align-items: center;
		margin-left: auto;
	}

	.input-wrapper {
		width: 200px;

		@media (max-width: 1050px) {
			width: 140px;
		}

		@media (max-width: 900px) {
			width: 130px;

			:deep(.input-element) {

				img {
					width: 14px;
					height: 14px;
				}

				input {
					height: 27px;
					padding-left: 32px;
				}
			}
		}

		@media (max-width: 420px) {
			width: 120px;
		}

	}
}
.table-edit-display {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	cursor: pointer;
	width: 100%;
    height: 100%;
	padding: 5px;
	background-color: var(--color-alpha-1);

	.standard-table-p {
		max-width: 100%;
		overflow-wrap: break-word;
		hyphens: auto;
		font-size: 12px;
		line-height: 16px;
		@include noSelect;
	}
}
.edit-number {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	padding: 5px;
	background-color: var(--color-bg-primary-2);

	input {
		width: 100%;
		height: 100%;
		border: none;
		color: var(--color-txt);
		font-size: 12px;
		background-color: var(--color-bg-primary-2);

	}
}
</style>